import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent, RefObject } from "react";

export interface ClearChatConfirmControls {
  /** True while the "Clear this conversation?" prompt is showing. */
  confirming: boolean;
  /** Shows the prompt and moves focus to its Cancel button, the safe default. */
  request: () => void;
  /** Hides the prompt and returns focus to "Clear chat". */
  cancel: () => void;
  /** Clears; on success focus goes to the prompt box, on failure back to "Clear chat". */
  confirm: () => Promise<void>;
  /** Escape cancels the prompt without closing the dock around it. */
  onPromptKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  triggerRef: RefObject<HTMLButtonElement | null>;
  cancelRef: RefObject<HTMLButtonElement | null>;
  /** The prompt box focus returns to after a clear: the caller's, or one this hook made. */
  composerRef: RefObject<HTMLTextAreaElement | null>;
  /** Links "Clear chat" to the prompt it opens (`aria-controls`), and names the prompt. */
  promptId: string;
}

const KEY_ESCAPE = "Escape";

/**
 * The confirm step in front of "Clear chat": inline in the dock rather than a modal, because the dock
 * is itself a dialog with its own focus trap and Escape, and a portalled modal would fight both.
 */
export function useClearChatConfirm(
  clearConversation: () => Promise<boolean>,
  callerComposerRef?: RefObject<HTMLTextAreaElement | null>,
): ClearChatConfirmControls {
  const [confirming, setConfirming] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const ownComposerRef = useRef<HTMLTextAreaElement | null>(null);
  const composerRef = callerComposerRef ?? ownComposerRef;
  const promptId = useId();

  useEffect(() => {
    if (confirming) cancelRef.current?.focus();
  }, [confirming]);

  const request = useCallback(() => setConfirming(true), []);

  const cancel = useCallback(() => {
    setConfirming(false);
    triggerRef.current?.focus();
  }, []);

  const confirm = useCallback(async () => {
    const cleared = await clearConversation();
    setConfirming(false);
    if (cleared) composerRef.current?.focus();
    else triggerRef.current?.focus();
  }, [clearConversation, composerRef]);

  const onPromptKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== KEY_ESCAPE) return;
    event.preventDefault();
    event.stopPropagation();
    cancel();
  }, [cancel]);

  return { confirming, request, cancel, confirm, onPromptKeyDown, triggerRef, cancelRef, composerRef, promptId };
}
